import {
  WIDGET_BUTTON_BOTTOM,
  WIDGET_BUTTON_COLOR,
  WIDGET_BUTTON_SHADOW,
  WIDGET_BUTTON_SIZE
} from 'shared/util/supportWidgetButton'

/**
 * DOM plumbing for the HubSpot chat widget, split out of `HubspotChat.tsx` so it can be tested.
 * The component keeps the event orchestration and the Sentry reporting.
 */

export const HS_LOADER_ID = 'hubspot-chat-loader'
export const HS_IFRAME_CONTAINER_ID = 'hubspot-messages-iframe-container'
export const HS_CONVERSATIONS_IFRAME_ID = 'hubspot-conversations-iframe'
export const HS_ZINDEX_STYLE_ID = 'hubspot-chat-zindex-cap'

// Just below Dialog's theme.zIndex.modal (2147483001) so the chat launcher/panel never renders on
// top of an open Dialog (e.g. the Confirm button in the Deal dialog). See #16376.
export const HS_MAX_Z_INDEX = '2147483000'

/**
 * HubSpot emits `open-change` while its iframe boots, so a close signal that arrives before the
 * panel has rendered is not the user closing anything. Acting on it hid a chat the user had just
 * opened (#22823). Close detection is armed when the panel iframe first appears, and this grace
 * window absorbs a boot message that lands right after.
 */
export const HS_CLOSE_GRACE_MS = 750

/** Matches where HubSpot's own launcher sits, so the placeholder does not jump when it arrives. */
const LOADER_RIGHT_PX = 16

/**
 * True when a close signal should hide the widget. `armedAt` is the timestamp the panel was seen
 * on screen, or `null` while nothing is open.
 */
export function isCloseActionable(armedAt: number | null, now: number, graceMs = HS_CLOSE_GRACE_MS) {
  if (armedAt === null) {
    return false
  }
  return now - armedAt >= graceMs
}

/**
 * HubSpot reads `hs-messages-is-open` to decide whether to restore the panel on load. Delete it on
 * every path first, so a stale duplicate from a deeper route cannot win, then set one clean value.
 */
export function setHsOpenCookie(value: boolean) {
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
  const paths = ['/', window.location.pathname]
  const segments = window.location.pathname.split('/').filter(Boolean)
  for (let i = 1; i <= segments.length; i++) {
    paths.push('/' + segments.slice(0, i).join('/'))
  }
  ;[...new Set(paths)].forEach((p) => {
    document.cookie = `hs-messages-is-open=;${expire};path=${p}`
  })
  document.cookie = `hs-messages-is-open=;${expire}`
  document.cookie = `hs-messages-is-open=${value}`
}

/**
 * Marks the stub widget below so the loader can tell it apart from HubSpot's own object. Without
 * this marker the stub satisfies every `HubSpotConversations.widget` check, and the load poll stops
 * waiting for the real script (ENG-1461).
 */
export const HS_STUB_MARKER = '__psHubspotStub'

/** Marks a widget whose `hide`/`show` we already replaced, so a second call cannot double-wrap. */
export const HS_GUARD_MARKER = '__psHubspotGuarded'

/**
 * Userflow's resource-center integration assumes `window.HubSpotConversations` always exists and
 * drives it on every resource-center open, whether or not HubSpot chat has ever been opened. We
 * load HubSpot lazily, only on demand, so on a session's first resource-center open that global is
 * undefined and Userflow's own bundled code throws — before our `ps:open-hubspot-chat` listener
 * ever runs (#22823 follow-up). A no-op stub prevents that throw; HubSpot's real script replaces
 * this global wholesale once chat is actually opened.
 *
 * Every member below is one the Userflow bundle reaches for. Read from
 * `ResourceCenterApp.v1.BnIdn5a4.js`, its HUBSPOT integration:
 *
 *     configure: … I.HubSpotConversations.on('unreadConversationCountChanged', y)
 *                  … I.HubSpotConversations.off('unreadConversationCountChanged', y)
 *     show: () => { …, I.HubSpotConversations.widget.open()  }
 *     hide: () => { …, I.HubSpotConversations.widget.close() }
 *
 * Stubbing only `hide`/`show` was not enough: Userflow's `hide` calls `widget.close`, so before
 * HubSpot loaded it threw `widget.close is not a function` instead (ENG-1461).
 *
 * The stub carries `HS_STUB_MARKER`. Read it through `isRealHubspotWidget` before treating a
 * `widget` as HubSpot's own.
 */
export function installHubspotConversationsStub() {
  if (!window.HubSpotConversations) {
    const noop = () => {}
    window.HubSpotConversations = {
      on: noop,
      off: noop,
      widget: {
        hide: noop,
        show: noop,
        open: noop,
        close: noop,
        load: noop,
        // False, so a caller that checks this never mistakes the stub for a loaded widget.
        status: () => ({ loaded: false }),
        [HS_STUB_MARKER]: true
      }
    } as unknown as Window['HubSpotConversations']
  }
}

/**
 * True only for HubSpot's own widget, never for the stub above.
 *
 * The stub is installed at module scope, so `HubSpotConversations.widget` is truthy from page load.
 * A plain truthiness check therefore reported "loaded" on the very first click, and the caller then
 * called `hs.on()` on a stub that has no `on` (ENG-1461).
 */
export function isRealHubspotWidget(
  hs?: Window['HubSpotConversations']
): hs is NonNullable<Window['HubSpotConversations']> {
  const widget = hs?.widget as Record<string, unknown> | undefined
  return Boolean(widget) && widget?.[HS_STUB_MARKER] !== true
}

/**
 * Takes the HubSpot widget's close controls away from Userflow, permanently.
 *
 * Userflow's resource center drives the HubSpot widget itself. Its bundle
 * (`ResourceCenterApp.v1.BnIdn5a4.js`) defines its HUBSPOT integration as:
 *
 *     show: () => { …, I.HubSpotConversations.widget.open()  }
 *     hide: () => { …, I.HubSpotConversations.widget.close() }
 *
 * So Userflow closes our panel through `widget.close()`. It never calls `widget.hide()`. #22934
 * read the PostHog stack traces (`Object.hide`, `Object.show`) as HubSpot method names and no-opped
 * `hide`/`show`; those frames are the names of Userflow's *own* integration methods, so the guard
 * covered two methods nobody calls and the chat kept closing itself (ENG-1461).
 *
 * `userflow.js@2.13.0` is a loader with no HubSpot surface, and Userflow's server-delivered config
 * switches the integration on, so there is no code-side switch to turn it off.
 *
 * What each method gets:
 *
 * - `close` is wrapped. `allowClose()` decides. A blocked call fires `onBlockedClose` and does
 *   nothing else. This is the fix — Userflow's `hide()` lands while the panel is still opening,
 *   and that is exactly when `allowClose()` is false.
 * - `hide` and `show` stay no-ops. The current bundle ignores them, but a future one may not.
 * - `open`, `load`, `status` and `remove` are left alone. Our own open path calls `widget.open()`,
 *   and if the Userflow resource-center item uses the built-in support-chat action (`show()` →
 *   `widget.open()`) rather than a custom `ps:open-hubspot-chat` dispatch, blocking `open` would
 *   stop the chat opening at all.
 *
 * HubSpot's in-panel close button closes inside the iframe and emits `widgetClosed`. It does not
 * call the public `widget.close()`, so blocking that call never blocks the user.
 *
 * Returns the names it took over, or `null` when there was nothing to do — no widget, or a widget
 * this already ran against. An empty array is the case worth reporting: the loaded script has none
 * of these methods, so the Userflow calls target something else again.
 */
export function guardUserflowWidgetControl(
  widget: NonNullable<Window['HubSpotConversations']>['widget'] | undefined,
  opts: { allowClose: () => boolean; onBlockedClose?: () => void }
): string[] | null {
  const target = widget as Record<string, unknown> | undefined
  if (!target || target[HS_GUARD_MARKER] === true) {
    return null
  }
  target[HS_GUARD_MARKER] = true

  const guarded = ['hide', 'show'].filter((name) => {
    if (typeof target[name] !== 'function') {
      return false
    }
    target[name] = () => {}
    return true
  })

  const realClose = target.close
  if (typeof realClose === 'function') {
    // Returns `void`, matching HubSpot's own `close(): void`. Forwarding the real return value
    // would widen the wrapper to `unknown` and mislead a future caller that inspects the type.
    target.close = (...args: unknown[]): void => {
      if (opts.allowClose()) {
        ;(realClose as (...a: unknown[]) => void).apply(target, args)
        return
      }
      opts.onBlockedClose?.()
    }
    guarded.push('close')
  }

  return guarded
}

/**
 * Calls `onHidden` when something writes `display: none` onto HubSpot's container.
 *
 * The teardown in `HubspotChat.tsx` is the only code here that hides the container, and it
 * disconnects this watcher first. So a callback means a third party hid the panel while we still
 * believed it was open, which leaves the user with no chat and no Userflow launcher (ENG-1461).
 * A `MutationObserver` is used instead of a poll for the reason `installHubspotZIndexCap` gives.
 *
 * Returns a disconnect function. Returns a no-op disconnect when the container is not in the DOM.
 */
export function watchHubspotContainerHidden(onHidden: () => void) {
  const el = document.getElementById(HS_IFRAME_CONTAINER_ID)
  if (!el || typeof MutationObserver === 'undefined') {
    return () => {}
  }
  const observer = new MutationObserver(() => {
    if (el.style.display === 'none') {
      observer.disconnect()
      onHidden()
    }
  })
  observer.observe(el, { attributes: true, attributeFilter: ['style'] })
  return () => observer.disconnect()
}

export function setHubspotVisible(visible: boolean) {
  const el = document.getElementById(HS_IFRAME_CONTAINER_ID)
  if (!el) {
    return
  }
  if (visible) {
    el.style.removeProperty('display')
  } else {
    el.style.setProperty('display', 'none', 'important')
  }
}

/**
 * Caps HubSpot's container z-index with a stylesheet rule instead of an inline style.
 *
 * HubSpot's script sets its own inline `z-index` (above our Dialog modal) and re-applies it
 * periodically. #22396 fought that with a 500 ms `setInterval` writing an inline
 * `z-index: … !important` onto HubSpot's element, which meant writing into a third-party widget's
 * DOM twice a second for the life of the page. An `!important` declaration in a stylesheet beats
 * HubSpot's non-`!important` inline value, so one rule holds the cap with no polling.
 */
export function installHubspotZIndexCap() {
  if (document.getElementById(HS_ZINDEX_STYLE_ID)) {
    return
  }
  const style = document.createElement('style')
  style.id = HS_ZINDEX_STYLE_ID
  style.textContent = `#${HS_IFRAME_CONTAINER_ID}{z-index:${HS_MAX_Z_INDEX} !important}`
  document.head.appendChild(style)
}

/** Placeholder launcher shown while HubSpot's script loads, so the corner is never empty. */
export function showChatLoader() {
  if (document.getElementById(HS_LOADER_ID)) {
    return
  }
  const loader = document.createElement('div')
  loader.id = HS_LOADER_ID
  Object.assign(loader.style, {
    position: 'fixed',
    bottom: `${WIDGET_BUTTON_BOTTOM}px`,
    right: `${LOADER_RIGHT_PX}px`,
    width: `${WIDGET_BUTTON_SIZE}px`,
    height: `${WIDGET_BUTTON_SIZE}px`,
    borderRadius: '50%',
    backgroundColor: WIDGET_BUTTON_COLOR,
    zIndex: HS_MAX_Z_INDEX,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: WIDGET_BUTTON_SHADOW
  })
  loader.innerHTML =
    '<div style="width:24px;height:24px;border:3px solid rgba(255,255,255,0.3);border-top-color:#fff;border-radius:50%;animation:hs-spin 0.8s linear infinite"></div>' +
    '<style>@keyframes hs-spin{to{transform:rotate(360deg)}}</style>'
  document.body.appendChild(loader)
}

export function hideChatLoader() {
  document.getElementById(HS_LOADER_ID)?.remove()
}
